import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { Trash2, Download, Images } from "lucide-react";
import { toast } from "sonner";
import { makeApi } from "@/lib/api";
import { useT } from "@/i18n";

const TX = {
  es: { title: "Mi galería de IA", empty: "Aquí aparecerán las imágenes que crees con IA.", del: "Imagen borrada", parent: "Galería de tu hijo/a", drawing: "Ver mi dibujo" },
  en: { title: "My AI gallery", empty: "The images you create with AI will appear here.", del: "Image deleted", parent: "Your child's gallery", drawing: "See my drawing" },
};

function useBlob(api, id) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!id) return undefined;
    let url;
    api.get(`/gallery/${id}/file`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [api, id]);
  return src;
}

function GalleryImage({ item, api, onDelete, readOnly, drawingLabel }) {
  const src = useBlob(api, item.id);
  const drawing = useBlob(api, item.drawing_id);
  const [showDrawing, setShowDrawing] = useState(false);
  return (
    <div className="group relative bg-white rounded-2xl border-4 border-white shadow-md overflow-hidden" data-testid={`gallery-item-${item.id}`}>
      <div className="aspect-square bg-sky-100 relative">
        {(showDrawing ? drawing : src) && <img src={showDrawing ? drawing : src} alt={item.prompt} className="w-full h-full object-cover" />}
        {drawing && (
          <button type="button" onClick={() => setShowDrawing((v) => !v)} className="absolute bottom-2 left-2 w-14 h-14 rounded-xl border-4 border-yellow-300 overflow-hidden shadow-lg bg-white" title={drawingLabel} data-testid={`gallery-drawing-${item.id}`}>
            <img src={showDrawing ? src : drawing} alt={drawingLabel} className="w-full h-full object-cover" />
          </button>
        )}
      </div>
      <p className="px-2 py-1.5 text-xs font-semibold text-blue-900 line-clamp-2" title={item.prompt}>{item.prompt}</p>
      <div className="absolute top-2 right-2 flex gap-1 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
        {src && <a href={src} download="lumi-spark-ia.png" className="bg-white/90 rounded-full p-1.5 shadow" data-testid={`gallery-download-${item.id}`}><Download className="w-4 h-4 text-blue-900" /></a>}
        {!readOnly && <button type="button" onClick={() => onDelete(item.id)} className="bg-white/90 rounded-full p-1.5 shadow" data-testid={`gallery-delete-${item.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></button>}
      </div>
    </div>
  );
}

export default function Gallery({ readOnly = false }) {
  const { getToken } = useAuth();
  const { lang } = useT();
  const tx = TX[lang] || TX.es;
  const [api] = useState(() => makeApi(getToken));
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/gallery").then((r) => setItems(r.data.items || [])).catch(() => {}), [api]);
  useEffect(() => {
    load();
    window.addEventListener("ailab-usage", load);
    return () => window.removeEventListener("ailab-usage", load);
  }, [load]);
  const onDelete = async (id) => {
    await api.delete(`/gallery/${id}`);
    setItems((v) => v.filter((i) => i.id !== id));
    toast.success(tx.del);
  };
  return (
    <div className="bg-white/70 rounded-[2rem] p-4 sm:p-6" data-testid="ai-gallery">
      <h3 className="font-fredoka text-2xl sm:text-3xl font-bold text-blue-950 inline-flex items-center gap-2">
        <Images className="w-7 h-7 text-violet-600" /> {readOnly ? tx.parent : tx.title}
        <span className="bg-violet-100 text-violet-900 rounded-full px-3 py-0.5 text-sm" data-testid="gallery-count">{items.length}</span>
      </h3>
      {items.length === 0 ? (
        <p className="mt-3 font-semibold text-blue-900/80" data-testid="gallery-empty">{tx.empty}</p>
      ) : (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {items.map((it) => <GalleryImage key={it.id} item={it} api={api} onDelete={onDelete} readOnly={readOnly} drawingLabel={tx.drawing} />)}
        </div>
      )}
    </div>
  );
}
